import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";
import { profileService } from "../services/profileService";
import { drillsService } from "../services/drillsService";
import { objectionsService } from "../services/objectionsService";
import { getLeaderboard, getUserLeaderboardStats } from "../services/leaderboardService";

/**
 * Prefetch nav routes on hover for instant transitions.
 * Warms up React Query cache without blocking UI.
 */
export function usePrefetchNav() {
  const queryClient = useQueryClient();
  const { user, profile } = useAuth();
  const { tenantId } = useTenant();

  const prefetchPath = (path) => {
    if (!user?.id) return;

    // Dashboard prefetch
    if (path === "/app/dashboard") {
      Promise.all([
        queryClient.prefetchQuery({
          queryKey: ["dashboard", "profile", user.id],
          queryFn: () => profileService.getUserProfile(),
        }),
        queryClient.prefetchQuery({
          queryKey: ["dashboard", "todaysDrill", user.id, profile?.level || 1, tenantId],
          queryFn: async () => {
            const prof = profile || (await profileService.getUserProfile());
            return drillsService.getTodaysDrillByLevel(prof?.level || 1, prof, tenantId);
          },
        }),
        queryClient.prefetchQuery({
          queryKey: ["dashboard", "drillHistory", user.id],
          queryFn: () => drillsService.getUserDrillHistory(100),
        }),
      ]).catch(() => {}); // Silently fail on prefetch
    }

    // Daily Drill
    if (path === "/app/daily-drill") {
      queryClient
        .prefetchQuery({
          queryKey: ["dailyDrill", user.id],
          queryFn: () => drillsService.getTodaysDrillByLevel(profile?.level || 1, profile, tenantId),
        })
        .catch(() => {});
    }

    // Objection Dojo
    if (path === "/app/objection-dojo") {
      queryClient
        .prefetchQuery({
          queryKey: ["objections", user.id],
          queryFn: () => objectionsService.getUserAttempts(user.id),
        })
        .catch(() => {});
    }

    // Leaderboards
    if (path === "/app/leaderboards" || path === "/app/gamification") {
      Promise.all([
        queryClient.prefetchQuery({
          queryKey: ["leaderboard", "global", "total_xp"],
          queryFn: () => getLeaderboard("global", "total_xp", { limit: 10 }),
        }),
        queryClient.prefetchQuery({
          queryKey: ["leaderboard", "userStats", user.id],
          queryFn: () => getUserLeaderboardStats(user.id),
        }),
      ]).catch(() => {});
    }
  };

  return { prefetchPath };
}
